import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, AlertCircle, Settings2 } from "lucide-react";
import { getProyeccionConfig, getProyeccionDrivers, saveProyeccionConfig } from "@/lib/api";

// ---------------------------------------------------------------------------
// Drivers → Generales (petición de Pau, nov-2026): las hipótesis GLOBALES de
// la proyección — impuesto, IPC, payout y nivel de proyección — se editan
// aquí y solo aquí (antes, en el popover «Parámetros» de Resultados). Mismos
// campos de config (`tasa_impuestos_pct`, `inflacion_pct`, `payout_pct`,
// `nivel_proyeccion`), así que no hay migración. Se guarda al salir de cada
// campo. Año base y horizonte siguen en la cabecera de Proyección; los ±pp en
// Escenarios; la NOF en su pestaña.
// ---------------------------------------------------------------------------

export const NIVEL_PROYECCION_LABEL = {
  auto: "Automático",
  "3D": "3 dígitos",
  MAX: "Máximo detalle",
};

const parsear = (texto) => {
  const t = String(texto ?? "").trim().replace(",", ".");
  if (t === "") return null;
  const v = parseFloat(t);
  return isNaN(v) ? undefined : v; // undefined = inválido
};

function Fila({ id, label, ayuda, children }) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[220px_160px_1fr] sm:items-center sm:gap-4">
      <Label htmlFor={id} className="text-sm">{label}</Label>
      {children}
      <p className="text-xs text-muted-foreground">{ayuda}</p>
    </div>
  );
}

export default function GeneralesContent({ empresaId, readOnly = false }) {
  const [config, setConfig] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [error, setError] = useState(null);
  const [tasa, setTasa] = useState("25");
  const [ipc, setIpc] = useState("");
  const [payout, setPayout] = useState("0");

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const [cfg, drv] = await Promise.all([
        getProyeccionConfig(empresaId),
        getProyeccionDrivers(empresaId).catch(() => ({ data: { drivers: [] } })),
      ]);
      setConfig(cfg.data);
      setDrivers(drv.data?.drivers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar la configuración de la proyección");
    }
  }, [empresaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!config) return;
    setTasa(config.tasa_impuestos_pct != null ? String(config.tasa_impuestos_pct) : "25");
    setIpc(config.inflacion_pct != null ? String(config.inflacion_pct) : "");
    setPayout(config.payout_pct != null ? String(config.payout_pct) : "0");
  }, [config]);

  const guardar = async (parcial, mensaje) => {
    try {
      await saveProyeccionConfig(empresaId, parcial);
      toast.success(mensaje);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
      await cargar();
    }
  };

  const handleTasaBlur = () => {
    const v = parsear(tasa);
    if (v === undefined || v === null || v < 0 || v > 100) {
      setTasa(config?.tasa_impuestos_pct != null ? String(config.tasa_impuestos_pct) : "25");
      return;
    }
    if (v === (config?.tasa_impuestos_pct ?? 25)) return;
    guardar({ tasa_impuestos_pct: v }, "Impuesto sobre beneficios actualizado");
  };

  const handleIpcBlur = () => {
    const v = parsear(ipc);
    if (v === undefined) {
      setIpc(config?.inflacion_pct != null ? String(config.inflacion_pct) : "");
      return;
    }
    if (v === (config?.inflacion_pct ?? null)) return;
    guardar({ inflacion_pct: v }, "IPC esperado actualizado");
  };

  const handlePayoutBlur = () => {
    const v = parsear(payout);
    if (v === undefined || v === null || v < 0 || v > 100) {
      setPayout(config?.payout_pct != null ? String(config.payout_pct) : "0");
      return;
    }
    if (v === (config?.payout_pct ?? 0)) return;
    guardar({ payout_pct: v }, "Payout de dividendos actualizado");
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!config) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  const nivel = config.nivel_proyeccion || "auto";
  // Drivers guardados que el nivel elegido deja fuera del cálculo (`activo`
  // del backend: el mismo criterio que marca las líneas en las tablas).
  const noAplicados = drivers.filter((d) => d.activo === false);

  return (
    <Card data-testid="generales-proyeccion">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-primary" />
          Generales
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Hipótesis globales de la proyección. Se guardan al salir de cada campo.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <Fila id="gen-tasa-impuestos" label="Impuesto sobre beneficios (%)" ayuda="Sobre el resultado antes de impuestos.">
          <Input
            id="gen-tasa-impuestos"
            type="number"
            min="0"
            max="100"
            step="0.5"
            className="h-9"
            value={tasa}
            onChange={(e) => setTasa(e.target.value)}
            onBlur={handleTasaBlur}
            disabled={readOnly}
            data-testid="tasa-impuestos"
          />
        </Fila>

        <Fila
          id="gen-ipc"
          label="IPC esperado (%)"
          ayuda={
            "Gastos sin driver; vacío = constantes. No cambia los drivers «Fijo + IPC», que llevan su propio %. " +
            "Si en «Defecto de proyección» eliges «Todo plano» o «Todo IPC», ese defecto manda sobre este valor."
          }
        >
          <Input
            id="gen-ipc"
            type="number"
            step="0.1"
            className="h-9"
            placeholder="Sin inflación"
            value={ipc}
            onChange={(e) => setIpc(e.target.value)}
            onBlur={handleIpcBlur}
            disabled={readOnly}
            data-testid="param-ipc"
          />
        </Fila>

        <Fila id="gen-payout" label="Dividendos — payout (%)" ayuda="Del resultado del año anterior.">
          <Input
            id="gen-payout"
            type="number"
            min="0"
            max="100"
            step="1"
            className="h-9"
            value={payout}
            onChange={(e) => setPayout(e.target.value)}
            onBlur={handlePayoutBlur}
            disabled={readOnly}
            data-testid="param-payout"
          />
        </Fila>

        <Fila
          id="gen-nivel"
          label="Nivel de proyección"
          ayuda="Automático: el driver más específico gana. Con 3 dígitos o Máximo detalle, los drivers del otro nivel quedan guardados pero no se aplican."
        >
          <select
            id="gen-nivel"
            className="h-9 w-full rounded-md border bg-background text-sm px-2"
            value={nivel}
            onChange={(e) => guardar({ nivel_proyeccion: e.target.value }, "Nivel de proyección actualizado")}
            disabled={readOnly}
            data-testid="param-nivel-proyeccion"
          >
            <option value="auto">{NIVEL_PROYECCION_LABEL.auto}</option>
            <option value="3D">{NIVEL_PROYECCION_LABEL["3D"]}</option>
            <option value="MAX">{NIVEL_PROYECCION_LABEL.MAX}</option>
          </select>
        </Fila>

        {noAplicados.length > 0 && (
          <Alert className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/30" data-testid="drivers-no-aplicados">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-sm">
              {noAplicados.length === 1
                ? "Hay 1 driver guardado que no se aplica por el nivel de proyección: "
                : `Hay ${noAplicados.length} drivers guardados que no se aplican por el nivel de proyección: `}
              {noAplicados.map((d) => `${d.codigo}${d.nombre ? ` (${d.nombre})` : ""}`).join(", ")}.
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground border-t pt-3">
          Año base y horizonte se eligen en la cabecera de Proyección; los ±pp de cada escenario, en
          Escenarios; las hipótesis de NOF, en la pestaña NOF.
        </p>
      </CardContent>
    </Card>
  );
}
